<script>
/**
 * 상품 등록 페이지.
 *
 * ProductDetailPage와 동일한 디자인으로 구성.
 * - 상품 기본정보 입력 (상품명, 카테고리, 통화, 임가공비, 중량, 상품설명)
 * - 썸네일 이미지 업로드 (등록 시 함께 업로드)
 * - 상세 이미지 업로드 (등록 시 함께 업로드)
 *
 * 등록 흐름: createProduct API 호출 → productCode 획득 → 이미지 순차 업로드 → 상세 페이지로 이동
 */
import { createProduct, uploadProductImage } from '@/api/product.js'
import { getCategoryList } from '@/api/category.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'ProductCreatePage',

  data() {
    return {
      /** 등록 폼 데이터 */
      form: {
        productName: '',
        categoryCode: '',
        productText: '',
        currency: '',
        conventionCost: null,
        weight: null,
        orderby: null,
      },

      /** 카테고리 선택 옵션 */
      categoryOptions: [{ title: '카테고리 없음', value: '' }],

      /** 썸네일 이미지 파일 목록 (등록 전 미리보기용) */
      thumbnailFiles: [],
      /** 상세 이미지 파일 목록 */
      detailFiles: [],

      /* ── UI 상태 ── */
      loading: false,
      saving: false,
      uploading: false,

      /** 오류 메시지 */
      errorMsg: '',
      saveErrorMsg: '',

      /** 로그인한 관리자 정보 */
      adminInfo: {},
    }
  },

  computed: {
    isHq() {
      return this.adminInfo?.adminType === 'HQ'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    this.fetchCategories()
  },

  beforeUnmount() {
    // 미리보기용 objectURL 해제 (메모리 누수 방지)
    ;[...this.thumbnailFiles, ...this.detailFiles].forEach(f => {
      if (f instanceof File && f._previewUrl) URL.revokeObjectURL(f._previewUrl)
    })
  },

  methods: {
    extractApiErrorMessage(error, fallback) {
      const data = error?.response?.data
      if (typeof data?.message === 'string' && data.message.trim()) return data.message
      if (typeof data?.error === 'string' && data.error.trim()) return data.error
      if (Array.isArray(data?.errors) && data.errors.length) {
        const first = data.errors[0]
        if (typeof first?.defaultMessage === 'string' && first.defaultMessage.trim()) return first.defaultMessage
        if (typeof first?.message === 'string' && first.message.trim()) return first.message
      }
      return fallback
    },

    async fetchCategories() {
      try {
        const res = await getCategoryList({ page: 1, size: 100 })
        if (res.data?.code === 0) {
          const list = res.data.data?.items || []
          this.categoryOptions = [
            { title: '카테고리 없음', value: '' },
            ...list.map(c => ({ title: c.categoryName, value: c.categoryCode })),
          ]
        }
      } catch (e) {
        console.error('[ProductCreatePage] fetchCategories error:', e)
      }
    },

    /** 썸네일 이미지 파일 선택 */
    onThumbnailSelect(event) {
      const files = Array.from(event.target.files || [])
      this.thumbnailFiles = [...this.thumbnailFiles, ...files]
      event.target.value = ''
    },

    /** 상세 이미지 파일 선택 */
    onDetailSelect(event) {
      const files = Array.from(event.target.files || [])
      this.detailFiles = [...this.detailFiles, ...files]
      event.target.value = ''
    },

    /** 썸네일 이미지 제거 */
    removeThumbnail(index) {
      this.thumbnailFiles.splice(index, 1)
    },

    /** 상세 이미지 제거 */
    removeDetail(index) {
      this.detailFiles.splice(index, 1)
    },

    /** 미리보기 URL 생성 (File → objectURL, 캐시하여 재사용) */
    getPreviewUrl(file) {
      if (!(file instanceof File)) return ''
      if (!file._previewUrl) file._previewUrl = URL.createObjectURL(file)
      return file._previewUrl
    },

    /** 등록 실행 */
    async handleCreate() {
      this.saveErrorMsg = ''
      this.errorMsg = ''

      if (!this.form.productName?.trim()) {
        this.saveErrorMsg = '상품명을 입력해 주세요.'
        return
      }
      if (this.form.orderby !== null && this.form.orderby !== '' && Number(this.form.orderby) < 0) {
        this.saveErrorMsg = '정렬순서는 0 이상이어야 합니다.'
        return
      }

      this.saving = true
      try {
        // 1. 상품 등록
        const createRes = await createProduct({
          productName: this.form.productName.trim(),
          categoryCode: this.form.categoryCode || null,
          productText: this.form.productText || null,
          currency: this.form.currency || null,
          conventionCost: this.form.conventionCost ?? null,
          weight:
            this.form.weight === '' || this.form.weight === null || this.form.weight === undefined
              ? null
              : Number(this.form.weight),
          orderby:
            this.form.orderby === '' || this.form.orderby === null || this.form.orderby === undefined
              ? null
              : Number(this.form.orderby),
        })

        if (createRes.data?.code !== 0) {
          this.saveErrorMsg = createRes.data?.message || '등록 중 오류가 발생했습니다.'
          return
        }

        const productCode = createRes.data?.productCode || createRes.data?.data?.productCode
        if (!productCode) {
          this.saveErrorMsg = '상품코드를 받지 못했습니다.'
          return
        }

        // 2. 썸네일 이미지 업로드
        this.uploading = true
        for (const file of this.thumbnailFiles) {
          try {
            await uploadProductImage(productCode, 'thumbnail', file)
          } catch (e) {
            console.error('[ProductCreatePage] thumbnail upload error:', e)
          }
        }

        // 3. 상세 이미지 업로드
        for (const file of this.detailFiles) {
          try {
            await uploadProductImage(productCode, 'detail', file)
          } catch (e) {
            console.error('[ProductCreatePage] detail upload error:', e)
          }
        }
        this.uploading = false

        // 4. 상세 페이지로 이동
        this.$router.push(`/products/${productCode}`)
      } catch (e) {
        console.error('[ProductCreatePage] handleCreate error:', e)
        this.saveErrorMsg = this.extractApiErrorMessage(e, '등록 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
        this.uploading = false
      }
    },

    /** 목록으로 돌아가기 */
    goBack() {
      this.$router.push('/products')
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 (ProductDetailPage와 동일 구조) -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="text-primary breadcrumb-link" @click="goBack">상품관리</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상품 등록</span>
        </div>
        <div class="text-h5 font-weight-bold">상품 등록</div>
        <div class="text-body-2 text-grey mt-1">새 상품 정보 및 이미지를 등록합니다.</div>
      </div>

      <div v-if="isHq" class="d-flex ga-2">
        <v-btn variant="text" :disabled="saving" @click="goBack">취소</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          :loading="saving || uploading"
          prepend-icon="mdi-check"
          @click="handleCreate"
        >
          {{ uploading ? '이미지 업로드 중...' : '등록' }}
        </v-btn>
      </div>
    </div>

    <!-- 오류 메시지 -->
    <v-alert
      v-if="saveErrorMsg"
      type="error"
      variant="tonal"
      density="compact"
      rounded="lg"
      class="mb-4"
    >
      {{ saveErrorMsg }}
    </v-alert>

    <!-- 상품 정보 카드 (ProductDetailPage 편집 모드와 동일 레이아웃) -->
    <v-card rounded="xl" elevation="0" class="detail-card mb-4">
      <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
        <div class="header-icon-wrap bg-primary-soft">
          <v-icon color="primary" size="18">mdi-package-variant</v-icon>
        </div>
        <span class="text-subtitle-2 font-weight-bold">상품 정보</span>
      </div>
      <v-divider />
      <v-card-text class="pa-5">
        <div class="edit-field-group">
          <div class="field-label">상품명 *</div>
          <v-text-field
            v-model="form.productName"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            maxlength="255"
            counter
            hide-details="auto"
            placeholder="상품명을 입력하세요"
          />
        </div>
        <div class="edit-field-group">
          <div class="field-label">카테고리</div>
          <v-select
            v-model="form.categoryCode"
            :items="categoryOptions"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            hide-details="auto"
          />
        </div>
        <div class="edit-field-group">
          <div class="field-label">통화</div>
          <v-text-field
            v-model="form.currency"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            placeholder="예: KRW, USD"
            hide-details="auto"
          />
        </div>
        <div class="edit-field-group">
          <div class="field-label">임가공비</div>
          <v-text-field
            v-model.number="form.conventionCost"
            type="number"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            placeholder="숫자 입력"
            hide-details="auto"
          />
        </div>
        <div class="edit-field-group">
          <div class="field-label">중량</div>
          <v-text-field
            v-model="form.weight"
            type="number"
            step="0.00001"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            placeholder="예: 7.77500 (미입력 시 0)"
            hide-details="auto"
          />
        </div>
        <div class="edit-field-group">
          <div class="field-label">정렬순서</div>
          <v-text-field
            v-model.number="form.orderby"
            type="number"
            min="0"
            step="1"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            placeholder="작을수록 먼저 노출 (미입력 시 기본값)"
            hide-details="auto"
          />
        </div>
        <div class="edit-field-group">
          <div class="field-label">상품설명</div>
          <v-textarea
            v-model="form.productText"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            rows="6"
            auto-grow
            hide-details="auto"
            placeholder="상품에 대한 설명을 입력하세요"
          />
        </div>

        <!-- 썸네일 이미지 업로드 -->
        <div class="edit-field-group">
          <div class="field-label">썸네일 이미지</div>
          <div class="d-flex flex-wrap ga-2 align-center">
            <div
              v-for="(file, idx) in thumbnailFiles"
              :key="'thumb-' + idx"
              class="image-item"
            >
              <v-img
                :src="getPreviewUrl(file)"
                width="120"
                height="120"
                cover
                class="rounded"
              />
              <v-btn
                icon
                size="x-small"
                color="error"
                variant="tonal"
                class="delete-btn"
                @click="removeThumbnail(idx)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <div  class="upload-slot upload-slot-detail">
              <input
                ref="thumbInput"
                type="file"
                accept="image/*"
                style="display:none"
                multiple
                @change="onThumbnailSelect"
              >
              <v-btn
                variant="tonal"
                size="small"
                color="success"
                @click="$refs.thumbInput?.click()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </div>
          <div class="text-caption text-medium-emphasis mt-1">썸네일로 사용할 이미지를 추가하세요.</div>
        </div>

        <!-- 상세 이미지 업로드 -->
        <div class="edit-field-group">
          <div class="field-label">상세 이미지</div>
          <div class="d-flex flex-wrap ga-2 align-center">
            <div
              v-for="(file, idx) in detailFiles"
              :key="'detail-' + idx"
              class="image-item"
            >
              <v-img
                :src="getPreviewUrl(file)"
                width="120"
                height="120"
                cover
                class="rounded"
              />
              <v-btn
                icon
                size="x-small"
                color="error"
                variant="tonal"
                class="delete-btn"
                @click="removeDetail(idx)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="upload-slot upload-slot-detail">
              <input
                ref="detailInput"
                type="file"
                accept="image/*"
                style="display:none"
                multiple
                @change="onDetailSelect"
              >
              <v-btn
                variant="tonal"
                size="small"
                color="success"
                @click="$refs.detailInput?.click()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </div>
          <div class="text-caption text-medium-emphasis mt-1">상세 페이지에 표시할 이미지를 추가하세요.</div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

.detail-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.detail-card-header {
  background: rgba(0, 0, 0, 0.018);
}

.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}
.bg-primary-soft {
  background: rgba(var(--v-theme-primary), 0.1);
}

.edit-field-group {
  margin-bottom: 16px;
}

.field-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.42);
  margin-bottom: 6px;
}

.image-item {
  position: relative;
}
.image-item .delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}
.upload-slot {
  min-width: 80px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}
.upload-slot-detail {
  min-width: 120px;
  min-height: 120px;
}
</style>
